/**
 * Lightweight multi-pixel conversion tracking for measuring paid-ad performance.
 *
 * No-ops entirely unless the relevant VITE_ pixel IDs are set — so dev, preview,
 * and any build without the env vars stay completely clean (no scripts loaded).
 *
 * ⚠️ COMPLIANCE: Reddit/Meta pixels are advertising/tracking technologies. Before
 * enabling them for UK/EU visitors you must (1) obtain cookie consent (PECR/GDPR)
 * and (2) disclose them in the privacy + cookie policy. See LEGAL_AUDIT.md #4.
 * `initAnalytics()` will only run after `setTrackingConsent(true)` is called, so
 * wiring a consent banner to that gate keeps this compliant.
 */

// Pixel IDs are PUBLIC values (they ship in the page HTML either way, like the
// Supabase publishable key). We hardcode a fallback so the pixel fires even when
// the Cloudflare env var isn't set — override via VITE_REDDIT_PIXEL_ID if needed.
const REDDIT_PIXEL_ID = (import.meta.env.VITE_REDDIT_PIXEL_ID as string | undefined) || "a2_jabk8qidbequ";
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

declare global {
  interface Window {
    rdt?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

const CONSENT_KEY = "mm-cookie-consent"; // "accepted" | "rejected" | absent

let initialized = false;
// Consent-gated: pixels only load if the user has previously accepted. No stored
// choice → off (banner will ask). This keeps us PECR/GDPR-compliant.
let consentGranted = (() => {
  try { return typeof window !== "undefined" && localStorage.getItem(CONSENT_KEY) === "accepted"; }
  catch { return false; }
})();

/** True once the user has made any cookie choice (accept or reject). */
export function hasCookieChoice(): boolean {
  try { return typeof window !== "undefined" && localStorage.getItem(CONSENT_KEY) != null; }
  catch { return false; }
}

export function setTrackingConsent(granted: boolean) {
  consentGranted = granted;
  try { localStorage.setItem(CONSENT_KEY, granted ? "accepted" : "rejected"); } catch { /* noop */ }
  if (granted) initAnalytics();
}

/** Injects whichever ad pixels are configured and fires the initial page view. */
export function initAnalytics() {
  if (initialized || !consentGranted || typeof window === "undefined") return;
  if (!REDDIT_PIXEL_ID && !META_PIXEL_ID) return; // nothing configured → stay clean
  initialized = true;

  if (REDDIT_PIXEL_ID) {
    (function (w: Window, d: Document) {
      if (w.rdt) return;
      const p = function (...args: unknown[]) {
        // @ts-expect-error runtime queue on the fn
        p.sendEvent ? p.sendEvent.apply(p, args) : p.callQueue.push(args);
      } as ((...a: unknown[]) => void) & { callQueue: unknown[][] };
      p.callQueue = [];
      w.rdt = p;
      const t = d.createElement("script");
      t.src = "https://www.redditstatic.com/ads/pixel.js";
      t.async = true;
      const s = d.getElementsByTagName("script")[0];
      s.parentNode?.insertBefore(t, s);
    })(window, document);
    window.rdt!("init", REDDIT_PIXEL_ID);
    window.rdt!("track", "PageVisit");
  }

  if (META_PIXEL_ID) {
    (function (f: Window, b: Document, e: string, v: string) {
      if (f.fbq) return;
      const n = function (...args: unknown[]) {
        // @ts-expect-error runtime methods on the fn
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      } as ((...a: unknown[]) => void) & { queue: unknown[][]; push: unknown; loaded: boolean; version: string };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
      f.fbq = n;
      const t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      const s = b.getElementsByTagName(e)[0];
      s.parentNode?.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    window.fbq!("init", META_PIXEL_ID);
    window.fbq!("track", "PageView");
  }
}

/** Fire once per browser when a user creates an account (the ad conversion goal). */
export function trackSignup() {
  try {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("mm-signup-tracked")) return; // idempotent — don't refire on later logins
    localStorage.setItem("mm-signup-tracked", "1");
    window.rdt?.("track", "SignUp");
    window.fbq?.("track", "CompleteRegistration");
  } catch { /* noop — analytics must never break the app */ }
}

/** Fire when a user completes a paid subscription. */
export function trackSubscribe(tier?: string, valueGbp?: number) {
  try {
    if (typeof window === "undefined") return;
    const money = valueGbp ? { value: valueGbp, currency: "GBP" } : undefined;
    window.rdt?.("track", "Purchase", tier ? { ...money, products: [{ id: tier }] } : money);
    window.fbq?.("track", "Subscribe", money);
  } catch { /* noop */ }
}
